import React, { useCallback, useEffect, useState } from 'react';
import { X, CheckCircle2, Pencil, ArrowRightLeft, Trash2, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, ReferenceLine, CartesianGrid } from 'recharts';
import { apiGet, apiPost, apiDel } from '../../lib/api';
import { formatINR, formatDate } from '../../lib/format';
import MachineForm from './MachineForm';

const inputCls =
  'w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:border-[#3b4872] focus:ring-1 focus:ring-[#3b4872] focus:outline-none';

const TABS = [
  ['overview', 'Overview'],
  ['plan', 'Service Plan'],
  ['hours', 'Hour Meter'],
  ['transfers', 'Transfers'],
  ['fuel', 'Fuel Efficiency'],
  ['util', 'Utilisation'],
  ['maint', 'Maintenance'],
];

export default function MachineDrawer({ equipment, initialTab = 'overview', onClose, onChanged }) {
  const [tab, setTab] = useState(initialTab);
  const [eq, setEq] = useState(equipment);
  const [dep, setDep] = useState(null);
  const [editOpen, setEditOpen] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [d, ds] = await Promise.all([
        apiGet(`/equipment/${equipment.id}/detail`),
        apiGet(`/equipment/${equipment.id}/depreciation-schedule`),
      ]);
      setEq(d); setDep(ds);
    } catch { /* keep row data */ }
  }, [equipment.id]);
  useEffect(() => { refresh(); }, [refresh]);

  const changed = () => { refresh(); onChanged?.(); };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-stretch justify-end" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-white w-full max-w-3xl flex flex-col" data-testid="eq-drawer">
        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872]">{eq.eq_id}</div>
            <h3 className="font-display font-bold text-xl mt-1">{eq.type} {(eq.make_model || eq.make) && `· ${eq.make_model || eq.make}`}</h3>
            <div className="text-xs text-slate-500 mt-1">
              Hour meter: <span className="font-display font-bold text-slate-700">{eq.current_hour_meter ?? 0} h</span>
              {' '}· Book value: <span className="font-display font-bold text-slate-700">{eq.purchase_value ? formatINR(eq.book_value ?? dep?.current_book_value) : '—'}</span>
              {' '}· Deployed: {eq.current_project_name || eq.location || '—'}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setEditOpen(true)} data-testid="eq-edit-btn"
              className="flex items-center gap-1 px-2.5 py-1.5 text-xs border border-slate-300 rounded-sm font-display font-semibold hover:border-[#3b4872] hover:text-[#3b4872]">
              <Pencil size={12} /> Edit
            </button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="eq-drawer-close"><X size={18} /></button>
          </div>
        </div>
        <div className="border-b border-slate-200 flex overflow-x-auto">
          {TABS.map(([t, label]) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`eq-drawer-tab-${t}`}
              className={`px-4 py-2.5 text-xs font-display font-semibold whitespace-nowrap border-b-2 -mb-px ${tab === t ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
              {label}
            </button>
          ))}
        </div>
        <div className="flex-1 overflow-y-auto">
          {tab === 'overview' && <OverviewSection eq={eq} dep={dep} />}
          {tab === 'plan' && <PlanSection eq={eq} onChanged={changed} />}
          {tab === 'hours' && <HoursSection eq={eq} onChanged={changed} />}
          {tab === 'transfers' && <TransfersSection eq={eq} onChanged={changed} />}
          {tab === 'fuel' && <FuelSection eq={eq} />}
          {tab === 'util' && <UtilSection eq={eq} onChanged={changed} />}
          {tab === 'maint' && <MaintSection eq={eq} onChanged={changed} />}
        </div>
      </div>
      {editOpen && (
        <MachineForm initial={eq} onClose={() => setEditOpen(false)} onSaved={() => { setEditOpen(false); changed(); }} />
      )}
    </div>
  );
}

const Overline = ({ children }) => <div className="kpi-overline mb-3">{children}</div>;
const EmptyNote = ({ children }) => <div className="text-xs text-slate-400 italic">{children}</div>;
const Field = ({ label, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-500 block mb-1.5">{label}</label>
    {children}
  </div>
);
const Info = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-display font-bold uppercase tracking-widest text-slate-400">{label}</div>
    <div className="text-sm text-slate-800 mt-0.5">{value ?? '—'}</div>
  </div>
);

function OverviewSection({ eq, dep }) {
  return (
    <div className="p-5 space-y-6">
      <div>
        <Overline>Purchase & Ownership</Overline>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-3" data-testid="eq-overview-grid">
          <Info label="Ownership" value={(eq.ownership || 'owned').toUpperCase()} />
          <Info label="Purchase Date" value={eq.purchase_date ? formatDate(eq.purchase_date) : '—'} />
          <Info label="Purchase Value" value={eq.purchase_value ? formatINR(eq.purchase_value) : '—'} />
          <Info label="Salvage Value" value={eq.salvage_value != null ? formatINR(eq.salvage_value) : '—'} />
          <Info label="Method" value={(eq.depreciation_method || 'slm').toUpperCase()} />
          <Info label={eq.depreciation_method === 'wdv' ? 'WDV Rate' : 'Useful Life'}
            value={eq.depreciation_method === 'wdv' ? (eq.wdv_rate_pct ? `${eq.wdv_rate_pct}%` : '—') : (eq.useful_life_years ? `${eq.useful_life_years} years` : '—')} />
          <Info label="Current Book Value" value={eq.purchase_value ? formatINR(dep?.current_book_value) : '—'} />
          <Info label="Internal Hire Rate" value={eq.internal_hire_rate_per_hour ? `${formatINR(eq.internal_hire_rate_per_hour)}/hr` : 'Not set'} />
          <Info label="Insurance Policy" value={eq.insurance_policy_id || '—'} />
        </div>
      </div>
      <div>
        <Overline>Depreciation Schedule</Overline>
        {!dep || !dep.schedule?.length ? <EmptyNote>Set purchase value + life (SLM) or WDV rate to see the schedule</EmptyNote> :
          <table className="w-full text-xs" data-testid="eq-dep-table">
            <thead className="bg-slate-50 border-b border-slate-200 text-[10px] font-display font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Year</th>
                <th className="px-3 py-2 text-right">Opening</th>
                <th className="px-3 py-2 text-right">Depreciation</th>
                <th className="px-3 py-2 text-right">Closing</th>
              </tr>
            </thead>
            <tbody>
              {dep.schedule.map(r => (
                <tr key={r.year} className="border-b border-slate-100">
                  <td className="px-3 py-1.5 font-mono">{r.year}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{formatINR(r.opening_value)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-red-600">− {formatINR(r.depreciation)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums font-display font-bold">{formatINR(r.closing_value)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
      </div>
    </div>
  );
}

function PlanSection({ eq, onChanged }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const plan = eq.maintenance_plan || [];
  const hm = Number(eq.current_hour_meter || 0);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost(`/equipment/${eq.id}/maintenance-plan`, {
        service_name: form.service_name,
        every_hours: Number(form.every_hours),
        last_done_at_hours: Number(form.last_done_at_hours || 0),
      });
      toast.success('Service added to plan');
      setForm(null); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };
  const done = async (item) => {
    setBusy(true);
    try {
      await apiPost(`/equipment/${eq.id}/maintenance-plan/${item.id}/done`);
      toast.success(`${item.service_name} marked done @ ${hm} h`);
      onChanged();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };
  const remove = async (item) => {
    try { await apiDel(`/equipment/${eq.id}/maintenance-plan/${item.id}`); toast.success('Removed'); onChanged(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <Overline>Hour-Meter Service Plan · current {hm} h</Overline>
        {!form && (
          <button onClick={() => setForm({})} data-testid="eq-plan-add-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold">
            <Plus size={11} /> Add Service
          </button>
        )}
      </div>
      {form && (
        <form onSubmit={add} className="border border-slate-200 rounded-sm p-3 bg-slate-50/50 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="eq-plan-form">
          <Field label="Service Name">
            <input value={form.service_name || ''} onChange={e => setForm({ ...form, service_name: e.target.value })} className={inputCls} data-testid="eq-plan-name" required placeholder="Engine oil change" />
          </Field>
          <Field label="Every (hours)">
            <input type="number" min="1" value={form.every_hours || ''} onChange={e => setForm({ ...form, every_hours: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-plan-every" required />
          </Field>
          <Field label="Last Done At (hours)">
            <input type="number" min="0" value={form.last_done_at_hours || ''} onChange={e => setForm({ ...form, last_done_at_hours: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-plan-lastdone" />
          </Field>
          <div className="flex items-end gap-2">
            <button type="submit" disabled={busy} data-testid="eq-plan-save" className="px-3 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50">Save</button>
            <button type="button" onClick={() => setForm(null)} className="px-2 py-2 text-xs text-slate-600">Cancel</button>
          </div>
        </form>
      )}
      {plan.length === 0 ? <EmptyNote>No service plan yet — add the OEM service intervals (250h / 500h / 2000h…)</EmptyNote> :
        <div className="space-y-3">
          {plan.map(item => {
            const every = Number(item.every_hours || 0);
            const sinceDone = Math.max(0, hm - Number(item.last_done_at_hours || 0));
            const pct = every > 0 ? Math.min(100, Math.round(sinceDone / every * 100)) : 0;
            const overdue = hm >= Number(item.next_due_at_hours || 0);
            const near = !overdue && hm >= Number(item.next_due_at_hours || 0) - 25;
            return (
              <div key={item.id} className="border border-slate-200 rounded-sm p-3" data-testid={`eq-plan-item-${item.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-display font-semibold">{item.service_name}</span>
                  <span className="text-xs text-slate-500">every {every} h · last @ {item.last_done_at_hours} h · next due @ {item.next_due_at_hours} h</span>
                  {overdue && <span className="px-1.5 py-0.5 text-[10px] bg-red-100 text-red-700 rounded-sm font-display font-bold uppercase">Overdue</span>}
                  {near && <span className="px-1.5 py-0.5 text-[10px] bg-amber-100 text-amber-700 rounded-sm font-display font-bold uppercase">Due Soon</span>}
                  <div className="ml-auto flex gap-1.5">
                    <button onClick={() => done(item)} disabled={busy} data-testid={`eq-plan-done-${item.id}`}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] bg-emerald-600 text-white rounded-sm font-display font-bold disabled:opacity-50">
                      <CheckCircle2 size={11} /> Mark Done
                    </button>
                    <button onClick={() => remove(item)} data-testid={`eq-plan-delete-${item.id}`}
                      className="px-1.5 py-1 text-slate-400 hover:text-red-600"><Trash2 size={12} /></button>
                  </div>
                </div>
                <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${overdue ? 'bg-red-500' : near ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
      }
    </div>
  );
}

function HoursSection({ eq, onChanged }) {
  const [logs, setLogs] = useState([]);
  const [reading, setReading] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet(`/equipment/${eq.id}/hour-meter-logs`).then(setLogs).catch(() => setLogs([]));
  }, [eq.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost(`/equipment/${eq.id}/hour-meter`, { reading: Number(reading), source: 'manual' });
      toast.success('Hour meter updated');
      setReading(''); load(); onChanged();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed';
      if (String(detail).includes("don't go backwards") && window.confirm(`${detail}\n\nForce this reading (meter replacement)?`)) {
        const reason = window.prompt('Reason for meter replacement:');
        if (reason) {
          try {
            await apiPost(`/equipment/${eq.id}/hour-meter`, { reading: Number(reading), source: 'manual', force: true, reason });
            toast.success('Hour meter force-updated');
            setReading(''); load(); onChanged();
          } catch (e2) { toast.error(e2?.response?.data?.detail || 'Failed'); }
        }
      } else toast.error(detail);
    }
    setBusy(false);
  };

  return (
    <div className="p-5 space-y-5">
      <form onSubmit={submit} className="flex items-end gap-3 border border-slate-200 rounded-sm p-3 bg-slate-50/50">
        <Field label={`New Reading (current ${eq.current_hour_meter ?? 0} h)`}>
          <input type="number" min="0" step="0.1" value={reading} onChange={e => setReading(e.target.value)}
            className={`${inputCls} tabular-nums w-44`} data-testid="eq-hm-input" required />
        </Field>
        <button type="submit" disabled={busy} data-testid="eq-hm-submit"
          className="px-3 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
          {busy ? 'Saving…' : 'Record Reading'}
        </button>
      </form>
      <div>
        <Overline>Reading History · {logs.length}</Overline>
        {logs.length === 0 ? <EmptyNote>No hour-meter readings recorded yet</EmptyNote> :
          <div className="space-y-1.5" data-testid="eq-hm-history">
            {logs.map(l => (
              <div key={l.id} className="flex items-center gap-3 text-xs border-b border-slate-100 pb-1.5">
                <span className="font-mono text-slate-500 w-36">{formatDate(l.created_at)}</span>
                <span className="tabular-nums font-display font-bold">{l.reading} h</span>
                <span className="text-slate-400">from {l.previous} h</span>
                <span className={`px-1.5 py-0.5 text-[10px] rounded-sm font-display font-bold uppercase ${l.source === 'fuel_log' ? 'bg-blue-50 text-blue-700' : l.source === 'utilisation' ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-600'}`}>{l.source}</span>
                {l.forced && <span className="px-1.5 py-0.5 text-[10px] bg-red-50 text-red-700 rounded-sm font-display font-bold uppercase" title={l.reason}>Forced</span>}
              </div>
            ))}
          </div>
        }
      </div>
    </div>
  );
}

function TransfersSection({ eq, onChanged }) {
  const [rows, setRows] = useState([]);
  const [projects, setProjects] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet(`/equipment/${eq.id}/transfers`).then(setRows).catch(() => setRows([]));
  }, [eq.id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    apiGet('/projects').then(r => setProjects(Array.isArray(r) ? r : (r.items || []))).catch(() => setProjects([]));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost(`/equipment/${eq.id}/transfer`, {
        to_project_id: form.to_project_id,
        transfer_date: form.transfer_date,
        reason: form.reason || '',
      });
      toast.success('Machine transferred');
      setForm(null); load(); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <div className="p-5 space-y-4">
      <div className="flex items-center justify-between">
        <Overline>Currently at: {eq.current_project_name || eq.location || '—'}</Overline>
        {!form && (
          <button onClick={() => setForm({ transfer_date: new Date().toISOString().slice(0, 10) })} data-testid="eq-transfer-btn"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold">
            <ArrowRightLeft size={11} /> Transfer to Project
          </button>
        )}
      </div>
      {form && (
        <form onSubmit={submit} className="border border-slate-200 rounded-sm p-3 bg-slate-50/50 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="eq-transfer-form">
          <Field label="To Project">
            <select value={form.to_project_id || ''} onChange={e => setForm({ ...form, to_project_id: e.target.value })}
              className={`${inputCls} bg-white`} data-testid="eq-transfer-project" required>
              <option value="">Select…</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </Field>
          <Field label="Transfer Date">
            <input type="date" value={form.transfer_date} onChange={e => setForm({ ...form, transfer_date: e.target.value })} className={inputCls} data-testid="eq-transfer-date" />
          </Field>
          <Field label="Reason">
            <input value={form.reason || ''} onChange={e => setForm({ ...form, reason: e.target.value })} className={inputCls} data-testid="eq-transfer-reason" />
          </Field>
          <div className="flex items-end gap-2">
            <button type="submit" disabled={busy} data-testid="eq-transfer-save" className="px-3 py-2 text-xs bg-[#3b4872] text-white rounded-sm font-display font-bold disabled:opacity-50">Transfer</button>
            <button type="button" onClick={() => setForm(null)} className="px-2 py-2 text-xs text-slate-600">Cancel</button>
          </div>
        </form>
      )}
      {rows.length === 0 ? <EmptyNote>No transfers recorded</EmptyNote> :
        <div className="space-y-2" data-testid="eq-transfer-history">
          {rows.map(t => (
            <div key={t.id} className="border-l-2 border-slate-200 pl-3 py-1.5 text-xs">
              <div className="flex items-center gap-2">
                <span className="font-mono text-slate-500">{formatDate(t.transfer_date)}</span>
                <span>{t.from_project_name || 'Unassigned'} → <span className="font-display font-bold">{t.to_project_name}</span></span>
                <span className="text-slate-400">@ {t.hour_meter_at_transfer} h</span>
              </div>
              {t.reason && <div className="text-slate-500 mt-0.5">{t.reason}</div>}
            </div>
          ))}
        </div>
      }
    </div>
  );
}

function FuelSection({ eq }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    apiGet(`/equipment/${eq.id}/fuel-efficiency`).then(setData).catch(() => setData({ months: [] }));
  }, [eq.id]);

  if (!data) return <div className="p-5 text-xs text-slate-400">Loading…</div>;
  const chart = (data.months || []).map(m => ({ ...m, lph: m.lph ?? null }));
  const hasData = chart.some(m => m.lph !== null);

  return (
    <div className="p-5 space-y-4" data-testid="eq-fuel-section">
      <div className="flex items-center gap-4 flex-wrap">
        <Info label="Machine Avg (6 mo)" value={data.machine_avg_lph != null ? `${data.machine_avg_lph} L/hr` : '—'} />
        <Info label={`Fleet Avg · ${data.type || ''}`} value={data.fleet_avg_lph != null ? `${data.fleet_avg_lph} L/hr (${data.peer_count} peers)` : 'No peers'} />
      </div>
      {data.flagged && (
        <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-sm text-xs text-red-700" data-testid="eq-fuel-flag">
          ⚠ {data.flag_reason}
        </div>
      )}
      {!hasData ? <EmptyNote>No fuel + run-hour data in the last 6 months — log utilisation with fuel, or refuel via the Fuel module</EmptyNote> :
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart} margin={{ top: 8, right: 12, bottom: 0, left: -16 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} unit=" L/h" />
              <Tooltip formatter={(v) => [`${v} L/hr`, 'Consumption']} />
              {data.fleet_avg_lph != null && (
                <ReferenceLine y={data.fleet_avg_lph} stroke="#94a3b8" strokeDasharray="4 4"
                  label={{ value: `Fleet avg ${data.fleet_avg_lph}`, fontSize: 10, fill: '#64748b', position: 'insideTopRight' }} />
              )}
              <Line type="monotone" dataKey="lph" stroke="#3b4872" strokeWidth={2} dot={{ r: 3 }} connectNulls />
            </LineChart>
          </ResponsiveContainer>
        </div>
      }
    </div>
  );
}

function UtilSection({ eq, onChanged }) {
  const [form, setForm] = useState({ date: new Date().toISOString().slice(0, 10), running_hours: 0, idle_hours: 0, downtime_hours: 0 });
  const [logs, setLogs] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet(`/equipment/${eq.id}/utilisation`).then(setLogs).catch(() => setLogs([]));
  }, [eq.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await apiPost(`/equipment/${eq.id}/utilisation`, {
        ...form,
        running_hours: Number(form.running_hours),
        idle_hours: Number(form.idle_hours),
        downtime_hours: Number(form.downtime_hours),
        fuel_consumed: Number(form.fuel_consumed || 0),
      });
      toast.success(`Util ${r.utilisation_pct}% logged`);
      load(); onChanged();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
    setBusy(false);
  };

  return (
    <>
      <form onSubmit={submit} className="p-5 border-b border-slate-200 grid grid-cols-2 gap-3 bg-slate-50/40">
        <div className="col-span-2 kpi-overline">Log Today's Utilisation</div>
        <Field label="Date">
          <input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} className={inputCls} data-testid="eq-util-date" />
        </Field>
        <Field label="Operator">
          <input value={form.operator || ''} onChange={e => setForm({ ...form, operator: e.target.value })} className={inputCls} data-testid="eq-util-operator" />
        </Field>
        <Field label="Running Hours">
          <input type="number" min="0" step="0.1" value={form.running_hours} onChange={e => setForm({ ...form, running_hours: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-util-running" required />
        </Field>
        <Field label="Idle Hours">
          <input type="number" min="0" step="0.1" value={form.idle_hours} onChange={e => setForm({ ...form, idle_hours: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-util-idle" />
        </Field>
        <Field label="Downtime Hours">
          <input type="number" min="0" step="0.1" value={form.downtime_hours} onChange={e => setForm({ ...form, downtime_hours: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-util-down" />
        </Field>
        <Field label="Fuel (L)">
          <input type="number" min="0" step="0.1" value={form.fuel_consumed || ''} onChange={e => setForm({ ...form, fuel_consumed: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-util-fuel" />
        </Field>
        <div className="col-span-2 flex justify-end">
          <button type="submit" disabled={busy} data-testid="eq-util-submit"
            className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Log Utilisation'}
          </button>
        </div>
      </form>
      <div className="p-5">
        <Overline>Recent Logs · {logs.length}</Overline>
        {logs.length === 0 ? <EmptyNote>No utilisation logs yet</EmptyNote> :
          <div className="space-y-1.5">
            {logs.slice(0, 30).map(u => (
              <div key={u.id} className="flex items-center justify-between text-xs border-b border-slate-100 pb-1.5">
                <span className="font-mono text-slate-500">{u.date}</span>
                <span className="tabular-nums">{u.running_hours}h <span className="text-slate-400">run</span></span>
                <span className="tabular-nums text-slate-500">{u.idle_hours}h <span className="text-slate-400">idle</span></span>
                <span className="tabular-nums text-red-600">{u.downtime_hours}h <span className="text-slate-400">down</span></span>
                <span className={`tabular-nums font-display font-bold ${u.utilisation_pct < 50 ? 'text-amber-600' : 'text-emerald-700'}`}>{u.utilisation_pct}%</span>
              </div>
            ))}
          </div>
        }
      </div>
    </>
  );
}

function MaintSection({ eq, onChanged }) {
  const [form, setForm] = useState({ kind: 'Preventive', scheduled_for: new Date().toISOString().slice(0, 10) });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    apiGet(`/equipment/${eq.id}/maintenance`).then(setHistory).catch(() => setHistory([]));
  }, [eq.id]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await apiPost(`/equipment/${eq.id}/maintenance`, {
        ...form,
        cost: Number(form.cost || 0),
        hours_meter: Number(form.hours_meter || 0),
      });
      toast.success('Maintenance scheduled');
      load(); onChanged();
    } catch { toast.error('Failed'); }
    setBusy(false);
  };

  return (
    <>
      <form onSubmit={submit} className="p-5 border-b border-slate-200 grid grid-cols-2 gap-3 bg-slate-50/40">
        <div className="col-span-2 kpi-overline">Schedule Maintenance (date-based)</div>
        <Field label="Kind">
          <select value={form.kind} onChange={e => setForm({ ...form, kind: e.target.value })} className={`${inputCls} bg-white`} data-testid="eq-maint-kind">
            {['Preventive', 'Breakdown', 'Corrective', 'Inspection'].map(k => <option key={k}>{k}</option>)}
          </select>
        </Field>
        <Field label="Scheduled For">
          <input type="date" value={form.scheduled_for} onChange={e => setForm({ ...form, scheduled_for: e.target.value })} className={inputCls} data-testid="eq-maint-date" />
        </Field>
        <Field label="Vendor">
          <input value={form.vendor || ''} onChange={e => setForm({ ...form, vendor: e.target.value })} className={inputCls} data-testid="eq-maint-vendor" />
        </Field>
        <Field label="Estimated Cost (₹)">
          <input type="number" min="0" value={form.cost || ''} onChange={e => setForm({ ...form, cost: e.target.value })} className={`${inputCls} tabular-nums`} data-testid="eq-maint-cost" />
        </Field>
        <Field label="Description" full>
          <textarea value={form.description || ''} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className={inputCls} data-testid="eq-maint-desc" placeholder="e.g. Service @ 500 hours: oil change, filter replacement, hydraulics check" />
        </Field>
        <div className="col-span-2 flex justify-end">
          <button type="submit" disabled={busy} data-testid="eq-maint-submit"
            className="px-4 py-2 text-sm bg-[#3b4872] text-white rounded-sm font-display font-semibold disabled:opacity-50">
            {busy ? 'Saving…' : 'Schedule'}
          </button>
        </div>
      </form>
      <div className="p-5">
        <Overline>Maintenance History · {history.length}</Overline>
        {history.length === 0 ? <EmptyNote>No maintenance records</EmptyNote> :
          <div className="space-y-2">
            {history.map(m => (
              <div key={m.id} className="border-l-2 border-slate-200 pl-3 py-1.5 text-xs">
                <div className="flex items-center gap-2 mb-0.5">
                  <span className="px-1.5 py-0.5 rounded-sm text-[10px] font-display font-bold uppercase tracking-widest bg-amber-100 text-amber-700">{m.kind}</span>
                  <span className="font-mono text-slate-500">{m.scheduled_for}</span>
                  <span className={`text-[10px] uppercase tracking-widest font-display font-bold ${m.status === 'Completed' ? 'text-emerald-700' : 'text-amber-600'}`}>{m.status}</span>
                </div>
                <div className="text-slate-700">{m.description || '—'}</div>
                {m.cost > 0 && <div className="text-slate-500">Cost: ₹{Number(m.cost).toLocaleString('en-IN')}</div>}
              </div>
            ))}
          </div>
        }
      </div>
    </>
  );
}
